// Migrada FAQ. Rendered on the homepage and /faq and published as FAQPage
// JSON-LD, so every answer must stay true to how Migrada works today.
// All answers must remain in the DOM at all times for SEO/LLM crawlability.

import { buddies } from "./buddies";
import { FEES } from "./fees";

const eur = (n: number) =>
  `€${n.toLocaleString("en-GB", { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;

export interface FAQItem {
  question: string;
  /** Parent questions get their own tab on the homepage FAQ. */
  audience?: "parents";
  // Answer as an array of paragraphs.
  answer: string[];
  // Internal links within this answer (rendered inline by the component)
  links?: { text: string; href: string }[];
}

export const faqItems: FAQItem[] = [
  {
    question: "What is Migrada?",
    answer: [
      "Migrada helps non-EU students move to Austria to study. You answer five quick questions and get your plan: your route, the real deadlines, what your documents need, the costs and your next steps.",
      "Your plan needs only your email. With a free account you follow your case, upload your documents to have them checked and ask the community.",
    ],
  },
  {
    question: "How much does Migrada cost?",
    answer: [
      "Nothing for students. The plan, your follow-up questions and the community are free.",
      "If a university or a partner ever pays us, we will tell you before you choose.",
    ],
  },
  {
    question: "How is my plan made?",
    answer: ["From your own answers and, once you upload them, your documents. It changes as you move forward."],
  },
  {
    question: "What documents do I need for an Austrian student residence permit?",
    answer: [
      "Non-EU students applying for a student residence permit in Austria need: a valid passport (if it runs out within 12 months, the permit is shortened to match and for the Visa D it must stay valid at least 3 months beyond the visa), a colour photo (45 × 35 mm, no older than 6 months), a university admission letter (Zulassungsbescheid) from an Austrian higher education institution, proof of financial means for 12 months in an account in your name that you can use from Austria, health insurance covering all risks and payable in Austria and a police clearance certificate no older than 3 months, which the authorities ask for in practice. A birth certificate and a rental contract are often requested too; documents that are not in German or English need a certified translation and many need an apostille or legalisation first.",
      "Which certification your documents need depends on your nationality. Your plan lists exactly what applies to your country.",
    ],
  },
  {
    question: "Which nationalities does Migrada help?",
    answer: [
      "Students of any non-EU nationality. Your plan is written for your country: whether your documents need an apostille or full legalisation, where you apply and how long it usually takes.",
      "Where a buddy from your country is available, they write your plan and answer your questions in your language.",
    ],
  },
  {
    question: "How long does the Austrian student residence permit take?",
    answer: [
      "The official processing time is up to 90 days from receipt of a complete application. Actual times vary by embassy. Western embassies (US, Canada, Japan, South Korea) typically process in 2 to 4 weeks. Turkish embassies process in 2 to 6 weeks. Indian and Chinese embassies take 4 to 8 weeks. Iranian embassies take 4 to 12 weeks. Central Asian embassies (Kazakhstan, Uzbekistan) take 2 to 4 weeks. African embassies (Nigeria, Egypt) take 6 to 12 weeks.",
      "Document preparation (translations, apostilles or legalisation, bank arrangements) adds weeks before you can even submit. Start at least 6 months before your planned arrival in Austria.",
    ],
  },
  {
    question: "Is Migrada a law firm?",
    answer: [
      "No. Migrada is not a law firm, does not give legal advice and does not represent anyone before an authority.",
      "Buddies share their own experience and general information from official sources. When your situation needs a lawyer, we tell you.",
    ],
  },
  {
    question: "What is an apostille and do I need one?",
    answer: [
      "An apostille is a certificate under the Hague Apostille Convention that confirms a public document is genuine. For Austria it only works if your country is a party to the Convention and Austria has not objected to its accession. For example, documents from Turkey, India, Kazakhstan, Russia, Ukraine, the United States, Canada, Brazil, South Korea and Japan need an apostille.",
      "If your country is not a party, or Austria objected to its accession, your documents need full legalisation instead: certification by your country's foreign ministry, then legalisation at the Austrian embassy. This applies, for example, to Iran, Nigeria, Turkmenistan, Uzbekistan, Pakistan and Bangladesh. Legalisation usually takes longer, so start early. Your plan tells you which applies to you.",
    ],
  },
  {
    question: "Can I apply after arriving in Austria?",
    answer: [
      "Only if you are from a visa-free country. Citizens of the United States, Canada, Brazil, Argentina, Chile, Japan, South Korea, Mexico and other visa-free nations can enter Austria without a visa for up to 90 days and apply at the local immigration office directly. In Vienna, this is the MA35 (Magistratsabteilung 35). In other cities, apply at the Bezirkshauptmannschaft.",
      "However, the OeAD recommends applying from abroad even if you are visa-free, because if processing exceeds 90 days, you may be required to leave the Schengen area. Students from visa-required countries (Turkey, Iran, India, China, Russia, Pakistan, Nigeria and most others) must apply at the Austrian Embassy in their home country before traveling to Austria.",
    ],
  },
  {
    question: "How much money do I need to prove?",
    answer: [
      "As of 2026, students under 24 must demonstrate at least €722.58 per month in available funds. Students aged 24 and over must show at least €1,308.39 per month. These funds must be proven for 12 months in advance, meaning approximately €8,671 (under 24) or €15,701 (24 and over) must be accessible in a bank account that works from Austria.",
      "If your monthly rent exceeds €386.43, additional funds are required to cover the excess. Acceptable proof is a bank or savings account in your name, a scholarship letter or a sponsor's declaration of liability (Haftungserklärung). A blocked account is not required. You must also show where the money comes from, such as parental employment certificates or loan documentation.",
    ],
  },
  {
    question: "What is an Aufenthaltstitel?",
    answer: [
      "Aufenthaltstitel is the German legal term for a residence permit in Austria. For students, the specific type is called Aufenthaltsbewilligung Student, which is a temporary residence permit issued to non-EU nationals enrolled at an Austrian university or accredited higher education institution.",
      "The Aufenthaltstitel is issued as a credit-card-sized biometric card after your application is approved. First-time applicants typically apply at the Austrian embassy in their home country. Renewals are handled by the local immigration authority in Austria (MA35 in Vienna, Bezirkshauptmannschaft in other cities). Your Migrada plan explains where and how you apply.",
    ],
  },
  {
    question: "What if my application is rejected?",
    answer: [
      "A rejected application means you must address the stated reason and reapply. Common rejection reasons include missing documents, insufficient financial proof, expired certificates, missing translations, or incomplete apostille or legalisation. Reapplying costs the full application fee (€218) again and restarts the processing timeline, which can delay your studies by 3 to 6 months.",
      "This is why preparing properly matters. Your plan lists what your documents need before you apply, so you can fix problems before the embassy sees them.",
    ],
  },
  // ── For parents ──
  {
    audience: "parents",
    question: "What does Migrada cost us as parents?",
    answer: ["Nothing. The plan, the follow-up questions and the community are free."],
  },
  {
    audience: "parents",
    question: "How much does one year in Austria need?",
    answer: [
      `Austria asks for proof of ${eur(FEES.FINANCIAL_UNDER_24_ANNUAL)} for one year of living costs if your child is under 24. Tuition at public universities is ${eur(FEES.TOTAL_SEMESTER)} a semester for most non-EU students and the residence permit costs ${eur(FEES.APPLICATION)}, card included.`,
    ],
  },
  {
    audience: "parents",
    question: "Who is talking to my child?",
    answer: [
      buddies.length > 0
        ? "Real people with real names: students from your country who study in Austria now and are part of the Migrada team. We never use made-up profiles."
        : "Our team and soon students from your country who study in Austria now and join the Migrada team. We never use made-up profiles.",
    ],
  },
  {
    audience: "parents",
    question: "Can you guarantee a university place or a visa?",
    answer: ["No and nobody honest can. Only the universities and the Austrian authorities decide. We help your child understand each step and prepare it properly."],
  },
  {
    audience: "parents",
    question: "Can I be part of it?",
    answer: ["Yes. Students under 18 add a parent's contact when they ask for a plan and the community has a topic just for parents."],
  },
  {
    audience: "parents",
    question: "What happens to our data and documents?",
    answer: [
      "We use your child's answers to write the plan. Documents are only uploaded if your child chooses to have them checked; only they and the person helping them can see them and they can be deleted at any time.",
    ],
  },
];

// JSON-LD schema for the FAQ page (for SEO / LLM crawlability)
export const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer.join(" "),
    },
  })),
};
